import React from 'react';
import { Link } from 'react-router-dom';
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'reversed';
type ButtonSize = 'sm' | 'md' | 'lg';
type ButtonShape = 'square' | 'pill';
interface BaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  className?: string;
  children: React.ReactNode;
}
interface LinkButtonProps extends BaseProps {
  to: string;
  href?: never;
  onClick?: () => void;
}
interface AnchorButtonProps extends BaseProps {
  href: string;
  to?: never;
  onClick?: () => void;
  target?: string;
  rel?: string;
}
interface ActionButtonProps extends BaseProps, Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> {
  to?: never;
  href?: never;
}
type ButtonProps = LinkButtonProps | AnchorButtonProps | ActionButtonProps;
const base = 'inline-flex items-center justify-center gap-2 font-medium transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60';
const variants: Record<ButtonVariant, string> = {
  // Dark text on the accent, so the brightest element on the page also carries
  // the strongest contrast.
  primary: 'bg-gold text-charcoal hover:bg-[#ff7440]',
  secondary: 'border border-line bg-panel text-ink hover:border-royal/60 hover:bg-[#123761]',
  ghost: 'text-royal hover:text-ink underline underline-offset-4 decoration-gold/70',
  reversed: 'border border-white/35 text-white hover:bg-white/10'
};
const sizes: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-small',
  md: 'px-5 py-3 text-[15px]',
  lg: 'px-7 py-4 text-[15px]'
};
export function Button(props: ButtonProps) {
  const {
    variant = 'primary',
    size = 'md',
    shape = 'square',
    className = '',
    children
  } = props;
  const radius = shape === 'pill' ? 'rounded-full' : 'rounded-sm';
  const classes = `${base} ${variant === 'ghost' ? '' : radius} ${variants[variant]} ${variant === 'ghost' ? '' : sizes[size]} ${className}`;
  if ('to' in props && props.to) {
    return <Link to={props.to} onClick={props.onClick} className={classes} data-id="element-430" data-mp="1haoh0y7lx4kc">
        {children}
      </Link>;
  }
  if ('href' in props && props.href) {
    return <a href={props.href} onClick={props.onClick} target={props.target} rel={props.rel ?? (props.target === '_blank' ? 'noopener noreferrer' : undefined)} className={classes} data-id="element-431" data-mp="1tfajpct4dna1">
        {children}
      </a>;
  }
  const {
    variant: _variant,
    size: _size,
    shape: _shape,
    className: _className,
    children: _children,
    ...rest
  } = props as ActionButtonProps;
  return <button {...rest} className={classes} data-id="element-432" data-mp="2h4dbhdhgwwi9">
      {children}
    </button>;
}